import { useEffect } from "react";
import FileUploader from "./fileUploader";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { fetchCaseById } from "../../store/user/userSlice";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";
import { isUrisBrand } from "../../utils/brandLogo";
import { fzPatientNamesFromDetails, isFzCaseId } from "../../utils/fzLab";
import UrisCaseFiles from "./UrisCaseFiles";

const CaseFileUpload = () => {
  const navigate = useNavigate();
  const { case_id } = useParams<{ case_id: string }>();
  const dispatch = useAppDispatch();

  const { user, boxAccessToken } = useAppSelector((state) => state.auth);
  const { caseFiles, fetching } = useAppSelector((state) => state.fileUpload);
  const { selectedCase } = useAppSelector((state) => state.userCases);
  const clientFiles =
    caseFiles?.filter(
      (file) => file.file_type?.toLowerCase() === "client_upload",
    ) ?? [];
  const hasClientUploads = clientFiles.length > 0;

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!case_id) return;

    const fetchData = async () => {
      await Promise.all([
        (!selectedCase || selectedCase.case_id !== case_id) &&
        dispatch(fetchCaseById(case_id)),
        !boxAccessToken && dispatch(fetchBoxAccessToken()),
      ]);
    };

    fetchData();
  }, [dispatch, case_id, selectedCase, boxAccessToken]);

  // Fetch files once both prerequisites are ready
  useEffect(() => {
    if (case_id && selectedCase && boxAccessToken) {
      dispatch(fetchCaseFilesByCaseId({ case_id }));
    }
  }, [dispatch, case_id, selectedCase, boxAccessToken]);

  const isUris = isUrisBrand(user);

  if (isFzCaseId(case_id)) {
    const fzNames = fzPatientNamesFromDetails(
      selectedCase?.case_id === case_id ? selectedCase?.details_json : null,
    );
    return (
      <div>
        <h2 className="text-xl md:text-3xl font-bold mb-2">
          FZ patient names for {selectedCase?.case_id || case_id}
        </h2>
        <p className="text-sm md:text-md text-amber-800 my-2 md:my-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          Select client files here — only their <strong>names</strong> are
          saved as patient names. Nothing is uploaded to Box.
        </p>
        <div className="mb-4 text-sm text-gray-800">
          <p className="mb-2 font-medium">
            Patient names : {fzNames.length}
          </p>
          {fzNames.length ? (
            <ul className="space-y-1">
              {fzNames.map((name) => (
                <li key={name} className="break-all">
                  {name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="italic text-gray-500">No patient names yet.</p>
          )}
        </div>
        <FileUploader
          userId={user?.user_id || 0}
          caseId={case_id || ""}
          file_type="CLIENT_UPLOAD"
          caseFolderId={selectedCase?.box_folder_id?.toString() || null}
          accessToken={boxAccessToken || ""}
          caseOwner={selectedCase?.case_owner || ""}
          dropTitle="Drop client files to add patient names"
          dropHint="Names only — no Box upload for FZ"
        />
      </div>
    );
  }

  return isUris && case_id ? (
    <UrisCaseFiles
      caseId={case_id}
      caseOwner={selectedCase?.case_owner || ""}
      caseFolderId={selectedCase?.box_folder_id}
    />
  ) : (
    <div>
      <h2 className="text-xl md:text-3xl font-bold mb-2">
        Step 2/2: Upload Case Files for {selectedCase?.case_id || "Loading..."}
      </h2>
      <p className="text-sm md:text-md text-gray-500 my-2 md:my-4">
        In case of errors, please try refreshing this page or reach out to{" "}
        <a href="mailto:admin@toothsketch.com" className="text-blue-500">
          admin@toothsketch.com
        </a>
      </p>

      <div aria-busy={fetching}>
        {fetching ? (
          <p>Loading files...</p>
        ) : hasClientUploads ? (
          <ul>
            {clientFiles.map((file, index) => (
              <li key={file.fileId || index}>{file.file_name}</li>
            ))}
          </ul>
        ) : (
          <p>No files uploaded yet.</p>
        )}
      </div>

      <div className="w-full h-full mt-2 md:mt-4 flex items-start justify-start">
        <FileUploader
          userId={user?.user_id || 0}
          caseId={case_id || ""}
          file_type="CLIENT_UPLOAD"
          caseFolderId={selectedCase?.box_folder_id?.toString() || null}
          accessToken={boxAccessToken || ""}
          caseOwner={selectedCase?.case_owner || ""}
        />
      </div>

      <div className="w-full h-full flex flex-col items-end gap-2 mt-2 md:mt-4">
        {!hasClientUploads && !fetching ? (
          <p className="text-sm text-amber-700">
            Upload at least one file before completing the case.
          </p>
        ) : null}
        <button
          className="bg-[#1DA8C6] text-stone-50 py-1 md:py-2 px-2 md:px-4 rounded-md text-md md:text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!hasClientUploads}
          onClick={() =>
            user?.role.toLowerCase() === "user"
              ? navigate("/dashboard/my-cases")
              : navigate("/dashboard/admin/cases")
          }
        >
          Complete Case
        </button>
      </div>
    </div>
  );
};

export default CaseFileUpload;